// A30 — post-Stripe-Checkout return page. Stripe redirects here on a
// successful checkout completion; we wait briefly for the webhook to
// reconcile the subscription row, then redirect to the home page.
//
// The webhook usually arrives within ~2s of the checkout completing.
// We poll /billing/subscription for up to 10s; once the tier flips to
// premium, redirect immediately. Otherwise fall through and trust SDB
// realtime to update the UI when the user lands on home.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError, getBillingSubscription } from "../api/client";

export function BillingReturnPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("Confirming subscription…");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    const POLL_INTERVAL_MS = 1000;
    const MAX_ATTEMPTS = 10;

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const sub = await getBillingSubscription();
        if (sub.tier === "premium") {
          setStatus("Premium activated. Returning to your workspace…");
          setTimeout(() => {
            if (!cancelled) navigate("/home");
          }, 800);
          return;
        }
      } catch (e) {
        setError(e instanceof ApiError ? e.message : String(e));
      }
      if (attempts >= MAX_ATTEMPTS) {
        setStatus(
          "Subscription is still processing — heading back. The webhook will land in a moment.",
        );
        setTimeout(() => {
          if (!cancelled) navigate("/home");
        }, 1500);
        return;
      }
      setTimeout(() => {
        if (!cancelled) void poll();
      }, POLL_INTERVAL_MS);
    };
    void poll();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  return (
    <div
      className="flex h-screen w-full items-center justify-center px-6"
      data-testid="billing-return-page"
    >
      <div className="w-full max-w-md rounded-md border border-edge bg-surface-raised p-6 text-center">
        <h1 className="text-lg font-semibold text-primary">Subscription</h1>
        <p className="mt-2 text-sm text-muted">{status}</p>
        {error && <p className="mt-3 text-xs text-danger">{error}</p>}
      </div>
    </div>
  );
}
